//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { ILike2BuyV2TikTokDefaultV1Config } from '@apis/like2buy-v2/like2BuyV2StoreModel';
import FileId from '@models/FileId';
import { ORDERED_INPUT_NAMES } from '@tiktok/components/like2buy/customizations/storeCustomizationsConstants';
import { ITikTokLike2BuyCustomizations } from '@tiktok/redux/like2buy/customizations/tiktokLike2BuyCustomizationsModel';
import { isDefined } from '@utils/filterPredicates';

export function tiktokStoreConfigToCustomizations(
  config: ILike2BuyV2TikTokDefaultV1Config,
): ITikTokLike2BuyCustomizations {
  const customStrings = config.customStringsByLocale['en-us'];

  return {
    banner: {
      altText: {
        text: customStrings?.gallery?.header?.bannerSection?.bannerAlt ?? '',
      },
      crawlId: config.galleryBannerSection?.crawlId,
      destinationUrl: {
        text:
          customStrings?.gallery?.header?.bannerSection?.destinationUrl ?? '',
      },
      imageUpload: {
        imageFileName: config.galleryBannerSection?.imageFileName,
        imageFileId: config.galleryBannerSection?.imageFileId
          ? FileId(config.galleryBannerSection.imageFileId)
          : undefined,
      },
      isBannerEnabled: config.galleryBannerSection?.isEnabled ?? false,
    },

    ctas: {
      galleryHeader: {
        text: customStrings?.gallery?.ctaBanner ?? '',
      },
      galleryMediaHover: {
        text: customStrings?.gallery?.item?.moreInfo ?? '',
      },
      productsContainer: {
        text: customStrings?.items?.products?.header ?? '',
      },
    },

    emailCapture: {
      cta: {
        text: customStrings?.gallery?.emailCapture?.cta ?? '',
      },
      isEnabled: config.galleryEmailCapture?.isEnabled ?? false,
      shouldRequireTermsAndConditions:
        config.galleryEmailCapture?.shouldRequireTermsAndConditions ?? false,
      termsAndConditionsUrl: {
        text: customStrings?.gallery?.emailCapture?.termsAndConditionsUrl ?? '',
      },
    },
  };
}

export function tiktokCustomizationsToStoreConfig(
  existingStoreConfig: ILike2BuyV2TikTokDefaultV1Config,
  customizations: ITikTokLike2BuyCustomizations,
): ILike2BuyV2TikTokDefaultV1Config {
  const {
    banner: {
      altText,
      crawlId,
      destinationUrl,
      imageUpload: { imageFileName, imageFileId },
      isBannerEnabled: isBannerEnabled,
    },
    ctas: { galleryHeader, galleryMediaHover, productsContainer },
    emailCapture: {
      cta: emailCaptureCta,
      isEnabled: isEmailCaptureEnabled,
      shouldRequireTermsAndConditions,
      termsAndConditionsUrl,
    },
  } = customizations;

  return {
    ...existingStoreConfig,

    galleryBannerSection: {
      crawlId: crawlId,
      imageFileId: imageFileId,
      imageFileName: imageFileName,
      isEnabled: isBannerEnabled,
    },

    galleryEmailCapture: {
      isEnabled: isEmailCaptureEnabled,
      shouldRequireTermsAndConditions,
    },

    customStringsByLocale: {
      ...existingStoreConfig.customStringsByLocale,
      'en-us': {
        ...existingStoreConfig.customStringsByLocale['en-us'],

        gallery: {
          ...existingStoreConfig.customStringsByLocale['en-us']?.gallery,

          header: {
            ...existingStoreConfig.customStringsByLocale['en-us']?.gallery
              ?.header,
            bannerSection: {
              ...existingStoreConfig.customStringsByLocale['en-us']?.gallery
                ?.header?.bannerSection,
              bannerAlt: altText.text || undefined,
              destinationUrl: destinationUrl.text || undefined,
            },
          },

          emailCapture: {
            ...existingStoreConfig.customStringsByLocale['en-us']?.gallery
              ?.emailCapture,
            cta: emailCaptureCta.text || undefined,
            termsAndConditionsUrl: termsAndConditionsUrl.text || undefined,
          },

          ctaBanner: galleryHeader.text || undefined,

          item: {
            ...existingStoreConfig.customStringsByLocale['en-us']?.gallery
              ?.item,
            moreInfo: galleryMediaHover.text || undefined,
          },
        },

        items: {
          ...existingStoreConfig.customStringsByLocale['en-us']?.items,

          products: {
            ...existingStoreConfig.customStringsByLocale['en-us']?.items
              ?.products,
            header: productsContainer.text || undefined,
          },
        },
      },
    },
  };
}

export function isCtaValid(value: string, limit: number): boolean {
  return value.length <= limit;
}

export function focusFirstInvalidFormInput(
  invalidInputs: typeof ORDERED_INPUT_NAMES,
  formEl: HTMLFormElement | null,
) {
  const firstInvalidInput = ORDERED_INPUT_NAMES.find((inputName) =>
    invalidInputs.includes(inputName),
  );

  if (isDefined(firstInvalidInput)) {
    (
      formEl?.elements.namedItem(firstInvalidInput) as HTMLElement | null
    )?.focus();
  }
}
